import React, { useContext, useEffect, useState } from 'react'

import { HistoryItems } from '../../../assets/ts/data/inquiry-history'
import { convertDate } from '../../../assets/ts/date-and-time/date-and-time'
import { defaultToEmptyString, formatStatusCodeToRole } from '../../../assets/ts/formatting/formatting'
import useDueoutHistory from '../../../hooks/dueout/use-dueout-history'
import { RoleContext } from '../../../store/role-context-provider'
import { UserContext } from '../../../store/user-context-provider'
import { DUEOUTS_STATUS } from '../../../utils/constants/global-constants'
import './progress.css'

const DueOutProgressBar = (props: any) => {
  const { DueOut, ItemGUID, role } = props

  const { isCustomer, isLoading } = useContext(RoleContext)
  const { me } = useContext(UserContext)
  const { dueOutHistory, refetch } = useDueoutHistory({ DueOutGUID: ItemGUID })
  const [historyItems, setHistoryItems] = useState<HistoryItems[]>([])
  const [loggedInUserId, setLoggedInUserId] = useState({})

  const formatHistoryList = async (historyData) => {
    let dueOutHistoryData 
    if (historyData.length > 1 && historyData[0].ActionCode !== 'CLOSE' && historyData[0].ActionCode !== 'CANCEL') {
      const firstItem = {...historyData[0]}
      firstItem.ActionCode = 'WAITING'
      dueOutHistoryData = [firstItem, ...historyData] 
    } else {
      dueOutHistoryData = [...historyData]
    }

    const currentUser = me?.userId // get logged in user
    setLoggedInUserId(currentUser)
    let waitingOnCSSorMPF
    let waitingOnAssignUserGroup = ''
    if (DueOut !== undefined && DueOut !== null) {
      const loggedInRole = role?.replace(DueOut['Section/MetadataValue'], '').trim()
      if (DueOut?.ServicingBy === 'CSS') {
        waitingOnCSSorMPF = 'CSS'
      } else if ( // MPF Dueout that is not assigned yet
        DueOut?.ServicingBy !== 'CSS' &&
        !DueOut?.AssignedTo &&
        DueOut?.Customer.userID !== currentUser
      ) {
        waitingOnCSSorMPF = isCustomer ? DueOut?.ServicingBy : loggedInRole //current user
      }
      const formattedData: any = []
  
      dueOutHistoryData.map(async (dueOutHistory, index) => {
        const formattedItem: any = {
          ...dueOutHistory,
          hideNameFromCustomer: true,
          isWaiting: false,
          itemKey: index,
          name: dueOutHistory.ItemCreatedBy?.properties.defaultDisplay,
          notStarted: false,
          waitingOnName: '',
        }
  
        switch (dueOutHistory.ActionCode) {
          case 'WAITING':
            formattedItem.waitingOnName = waitingOnCSSorMPF? waitingOnCSSorMPF : 'Customer'
            formattedItem.name = DueOut?.AssignedTo?.properties.defaultDisplay
            formattedItem.isWaiting = true
            formattedItem.hideNameFromCustomer = isCustomer
            if (
              (DueOut?.StatusCode === DUEOUTS_STATUS.IN_PROGRESS ||
              DueOut?.StatusCode === DUEOUTS_STATUS.NOT_STARTED)
            ) {
              formattedItem.waitingOnName = 'Customer'
              formattedItem.name = ''
            }
            formattedItem.UserRole = formattedItem?.UserRole.replace(DueOut['Section/MetadataValue'], '').trim()
            break
          case 'SUBMIT':
            formattedItem.UserRole = dueOutHistory?.ServicingBy
            if (dueOutHistoryData.length === 1) formattedItem.notStarted = true
            break
          case 'ACKNOWLEDGED':
            if (index === 0) formattedItem.isWaiting = true
            formattedItem.waitingOnName = 'Customer'
            formattedItem.Action = 'In Progress'
            break
          case 'RESPOND':
            formattedItem.waitingOnName = 'Customer'
            formattedItem.Action = 'Responded'
            formattedItem.UserRole = formattedItem.UserRole.replace(DueOut['Section/MetadataValue'], '').trim()
            formattedItem.hideNameFromCustomer = isCustomer
            break
          case 'COMPLETE':
            formattedItem.Action = 'Completed'
            formattedItem.hideNameFromCustomer = isCustomer
            formattedItem.waitingOnName = waitingOnCSSorMPF
            formattedItem.UserRole = formattedItem.UserRole.replace(DueOut['Section/MetadataValue'], '').trim()
            break
          case 'ASSIGN_TO_SELF':
          case 'ASSIGN_TO_OTHER':
            if (dueOutHistory.StatusCode === DUEOUTS_STATUS.COMPLETED) {
              formattedItem.waitingOnName = waitingOnCSSorMPF
            }
            formattedItem.hideNameFromCustomer = isCustomer
            formattedItem.name = dueOutHistory.AssignedTo?.properties.defaultDisplay
            formattedItem.Action = 'Assigned'
            formattedItem.UserRole = formattedItem.UserRole.replace(DueOut['Section/MetadataValue'], '').trim()
            if( waitingOnAssignUserGroup === '' && DueOut?.StatusCode === DUEOUTS_STATUS.COMPLETED) {
              waitingOnAssignUserGroup = formatStatusCodeToRole(dueOutHistory.WaitingOnUserRole)
              formattedData[0].waitingOnName = 
              waitingOnAssignUserGroup.includes('CSS') ? 'CSS' : waitingOnAssignUserGroup
            }
            break
          case 'CLOSE':
            formattedItem.Action = 'Closed'
            formattedItem.waitingOnName = waitingOnCSSorMPF
            formattedItem.UserRole = formattedItem.UserRole.replace(DueOut['Section/MetadataValue'], '').trim()
            formattedItem.hideNameFromCustomer = isCustomer
            break
          case 'CANCEL':
            formattedItem.Action = 'Canceled'
            formattedItem.waitingOnName = waitingOnCSSorMPF
            formattedItem.UserRole = formattedItem.UserRole.replace(DueOut['Section/MetadataValue'], '').trim()
            break
          default:
            break
        }
        formattedData.push(formattedItem)
      })
      setHistoryItems(formattedData)
    }
  }

  useEffect(() => {
    if (!isLoading) formatHistoryList(dueOutHistory)
  }, [dueOutHistory, DueOut, isCustomer, isLoading, role])

  useEffect(() => {
    const currentAction = historyItems?.[0]?.Action
    if (currentAction) {
      props.setCurrentAction(currentAction)
    }
  }, [historyItems])
  useEffect(() => {
    if (props.refresh) {
      // Call refetch when refresh is changed
      refetch()
    }
  }, [props.refresh])
  return (
    <>
      <div className='card-header border-bottom1 d-flex align-items-center'>
        <div
          aria-label='Due Out Status'
          className='title-color20 poppins-semibold font-14'
          tabIndex={0}
        >
          Due Out Status
        </div>
      </div>
      <div className='card-body'>
        <div className='ms-auto'>
          <ul className='list-type-none progress v-steps w-100'>
            {historyItems?.map((historyItem: any, index) => {
              // Generate unique key for each li element
              const baseKey = `${
                historyItem.DueOutID || historyItem.DueOutGUID || historyItem.ItemModified
              }-${index}`

              return (
                <React.Fragment key={baseKey}>
                  {historyItem.isWaiting && (
                    <li className='px-2 px-xl-3' key={`${baseKey}-waiting`}>
                      <div className='progress-steps'>
                        <div className='circle'> </div>
                        <div
                          aria-live='polite'
                          className='progress-title pb-1 d-flex flex-column ps-4'
                          tabIndex={0}
                        >
                          <span className='ttile-color poppins-semibold font-13'>
                            {defaultToEmptyString(historyItem.waitingOnName)}
                          </span>
                          {historyItem.waitingOnName === 'Customer' && (
                            <span className='title-color23 sourcesanspro font-12 '>
                              {defaultToEmptyString(DueOut?.Customer.properties.defaultDisplay)}
                            </span>
                          )}
                          {historyItem.StatusCode === DUEOUTS_STATUS.COMPLETED &&
                            !isCustomer && (
                              <span className='title-color23 sourcesanspro font-12 '>
                                {defaultToEmptyString(historyItem.name)}
                              </span>
                            )}
                          <span className='title-color23 sourcesanspro font-12 '>
                            {defaultToEmptyString(
                              convertDate(historyItem.ItemCreated, 'dateFormat')
                            )}
                          </span>
                          <span className='border-primary maxfitcontent color-primary mt-1 px-2 py-1 border-radius sourcesanspro font-13 text-uppercase'>
                            Waiting
                          </span>
                        </div>
                      </div>
                    </li>
                  )}
                  {historyItem.notStarted && (
                    <li className='px-2 px-xl-3' key={`${baseKey}-notStarted`}>
                      <div className='progress-steps'>
                        <div className='circle'> </div>
                        <div
                          aria-live='polite'
                          className='progress-title pb-1 d-flex flex-column ps-4'
                          tabIndex={0}
                        >
                          <span className='ttile-color poppins-semibold font-13'>Customer</span>
                          <span className='title-color23 sourcesanspro font-12 '>
                            {defaultToEmptyString(DueOut?.Customer?.properties?.defaultDisplay)}
                          </span>
                          <span className='title-color23 sourcesanspro font-12 '>
                            {defaultToEmptyString(
                              convertDate(historyItem.ItemCreated, 'dateFormat')
                            )}
                          </span>
                          <span className='border-primary maxfitcontent color-primary mt-1 px-2 py-1 border-radius sourcesanspro font-13 text-uppercase'>
                            Not Started
                          </span>
                        </div>
                      </div>
                    </li>
                  )}
                {!historyItem.isWaiting &&(<li className='px-2 px-xl-3' key={`${baseKey}-action`}>
                  <div className='progress-steps'>
                    <div className='circle outeractive'> </div>
                    <div
                      aria-live='polite'
                      className='progress-title pb-1 d-flex flex-column ps-4'
                      tabIndex={0}
                    >
                      <span className='ttile-color poppins-semibold font-13'>
                        {defaultToEmptyString(historyItem.UserRole)}
                      </span>
                      {(DueOut?.Customer.userID !== loggedInUserId ||
                        (!historyItem.hideNameFromCustomer)) && (
                        <span className='title-color23 sourcesanspro font-12 '>
                          {defaultToEmptyString(historyItem.name)}
                        </span>
                      )}
                      {historyItem.UserRole === 'Customer' &&
                        DueOut?.Customer.userID === loggedInUserId && (
                          <span className='title-color23 sourcesanspro font-12 '>
                            {defaultToEmptyString(historyItem.name)}
                          </span>
                        )}
                      <span className='title-color23 sourcesanspro font-12 '>
                        {defaultToEmptyString(convertDate(historyItem.ItemCreated, 'dateFormat'))}
                      </span>
                      {index !== historyItems.length - 1 && (
                        <span className='border-primary maxfitcontent color-primary mt-1 px-2 py-1 border-radius sourcesanspro font-13 text-uppercase'>
                          {defaultToEmptyString(historyItem.Action)}
                        </span>
                      )}
                    </div>
                  </div>
                </li>)}
                </React.Fragment>
              )
            })}
          </ul>
        </div>
      </div>
    </>
  )
}
export default DueOutProgressBar
